import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import {
  apiService,
  InfrastructureRequest,
  Project,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { draftFromInfrastructureRequest, tenantLabelFromInfrastructureRequest } from "./infraDraft";
import { formatTerraformForCopy } from "./terraformPreview";
import { InfraEditSession } from "./types";

type Props = {
  onModify: (session: InfraEditSession) => void;
};

type ViewMode = "project" | "all";
type LifecycleFilter = "active" | "destroyed";

const VIEW_MODE_OPTIONS: Array<{ label: string; value: ViewMode }> = [
  { label: "Single project", value: "project" },
  { label: "All projects", value: "all" },
];

const LIFECYCLE_OPTIONS: Array<{ label: string; value: LifecycleFilter }> = [
  { label: "Active", value: "active" },
  { label: "Destroyed", value: "destroyed" },
];

const isInactive = (row: InfrastructureRequest) =>
  row.status === "destroyed" || row.status === "canceled";

const isTerminalStatus = (status: string) =>
  status === "destruction_requested" ||
  status === "destroyed" ||
  status === "canceled";

const ViewInfrastructures: React.FC<Props> = ({ onModify }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [lifecycle, setLifecycle] = useState<LifecycleFilter>("active");
  const [selectedReference, setSelectedReference] = useState<string | null>(
    null,
  );
  const [items, setItems] = useState<InfrastructureRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<InfrastructureRequest | null>(null);
  const [destroyingId, setDestroyingId] = useState<number | null>(null);
  const [destroyTarget, setDestroyTarget] =
    useState<InfrastructureRequest | null>(null);
  const [destroyConfirmCode, setDestroyConfirmCode] = useState("");
  const toast = useToast();

  useEffect(() => {
    (async () => {
      const res = await apiService.getProjects();
      if (res.success) setProjects(res.data || []);
      else toast.showError("Failed", res.message || "Unable to load projects");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadForProject = async (reference: string) => {
    setLoading(true);
    const res = await apiService.listProjectInfrastructure(reference);
    if (res.success) setItems(res.data || []);
    else {
      setItems([]);
      toast.showError("Failed", res.message || "Unable to load infrastructure");
    }
    setLoading(false);
  };

  const loadAllLatest = async () => {
    setLoading(true);
    const res = await apiService.listInfrastructure();
    if (res.success) setItems(res.data || []);
    else {
      setItems([]);
      toast.showError("Failed", res.message || "Unable to load infrastructure");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (viewMode === "all") {
      loadAllLatest();
    } else if (selectedReference) {
      loadForProject(selectedReference);
    } else {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const projectOptions = projects.map((p) => ({
    label: `${p.reference || ""} — ${p.name}`,
    value: p.reference || "",
  }));

  const projectNameByReference = useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projects) {
      if (project.reference) map.set(project.reference, project.name);
    }
    return map;
  }, [projects]);

  const projectLabel = (reference: string) => {
    const name = projectNameByReference.get(reference);
    return name ? `${reference} — ${name}` : reference;
  };

  const filteredItems = useMemo(
    () =>
      items.filter((row) =>
        lifecycle === "destroyed" ? isInactive(row) : !isInactive(row),
      ),
    [items, lifecycle],
  );

  const startModify = (row: InfrastructureRequest) => {
    const draft = draftFromInfrastructureRequest(row);
    onModify({
      basedOnId: row.id,
      draft,
      tenantLabel: tenantLabelFromInfrastructureRequest(row),
    });
    toast.showInfo(
      "Modify",
      `Loaded infrastructure #${row.id}. Submit to create a new version.`,
    );
  };

  const basedOnId = (row: InfrastructureRequest): number | null => {
    const raw = row.request_json as { based_on_id?: number } | null;
    return raw && typeof raw.based_on_id === "number" ? raw.based_on_id : null;
  };

  const refresh = () => {
    if (viewMode === "all") loadAllLatest();
    else if (selectedReference) loadForProject(selectedReference);
  };

  const openDestroyDialog = (row: InfrastructureRequest) => {
    setDestroyTarget(row);
    setDestroyConfirmCode("");
  };

  const closeDestroyDialog = () => {
    setDestroyTarget(null);
    setDestroyConfirmCode("");
  };

  const destroyCodeMatches =
    !!destroyTarget &&
    destroyConfirmCode.trim() === destroyTarget.project_reference;

  const submitDestruction = async () => {
    if (!destroyTarget || !destroyCodeMatches) return;
    const row = destroyTarget;
    closeDestroyDialog();
    setDestroyingId(row.id);
    const res = await apiService.requestInfrastructureDestruction(row.id);
    setDestroyingId(null);
    if (res.success) {
      const phishing = res.data.phishing_domains_to_deregister || [];
      toast.showSuccess(
        "Destruction requested",
        phishing.length > 0
          ? `Notified admins. Phishing domains flagged: ${phishing.join(", ")}`
          : "Notified admins. No phishing domains to deregister.",
      );
      refresh();
    } else {
      toast.showError(
        "Destruction request failed",
        res.message || "Unable to request destruction",
      );
    }
  };

  const emptyMessage =
    viewMode === "all"
      ? lifecycle === "destroyed"
        ? "No destroyed or canceled infrastructure requests"
        : "No active infrastructure requests"
      : selectedReference
        ? lifecycle === "destroyed"
          ? "No destroyed or canceled infrastructure for this project"
          : "No active infrastructure for this project"
        : "Select a project to view infrastructure";

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 align-items-center justify-content-between">
        <h2 className="m-0 text-xl">View infrastructures</h2>
        <div className="flex flex-wrap gap-2 align-items-center">
          <SelectButton
            value={viewMode}
            options={VIEW_MODE_OPTIONS}
            onChange={(e) => {
              if (!e.value) return;
              setViewMode(e.value);
              if (e.value === "project") setItems([]);
            }}
            allowEmpty={false}
          />
          <SelectButton
            value={lifecycle}
            options={LIFECYCLE_OPTIONS}
            onChange={(e) => {
              if (!e.value) return;
              setLifecycle(e.value);
            }}
            allowEmpty={false}
          />
          {viewMode === "project" && (
            <Dropdown
              value={selectedReference}
              options={projectOptions}
              onChange={(e) => {
                setSelectedReference(e.value);
                if (e.value) loadForProject(e.value);
                else setItems([]);
              }}
              placeholder="Select project (name + code)"
              filter
              className="w-20rem"
            />
          )}
          <Button
            icon="pi pi-refresh"
            className="p-button-outlined"
            disabled={viewMode === "project" && !selectedReference}
            loading={loading}
            onClick={refresh}
          />
        </div>
      </div>

      {viewMode === "all" && (
        <p className="text-600 text-sm mt-0 mb-3">
          {lifecycle === "active"
            ? "Showing the latest active infrastructure request per project and tenant."
            : "Showing the latest destroyed or canceled infrastructure request per project and tenant."}
        </p>
      )}

      <DataTable
        value={filteredItems}
        loading={loading}
        emptyMessage={emptyMessage}
        size="small"
      >
        <Column field="id" header="ID" style={{ width: "5rem" }} />
        <Column
          field="project_reference"
          header="Project"
          body={(row: InfrastructureRequest) =>
            projectLabel(row.project_reference)
          }
        />
        <Column field="status" header="Status" />
        <Column
          header="Tenant"
          body={(row: InfrastructureRequest) =>
            tenantLabelFromInfrastructureRequest(row) || row.tenant_id || "—"
          }
        />
        <Column field="client_key" header="Client key" />
        <Column
          header="Based on"
          body={(row: InfrastructureRequest) => basedOnId(row) ?? "—"}
        />
        <Column field="created_by_email" header="Created by" />
        <Column field="created_at" header="Created" />
        <Column
          header=""
          body={(row: InfrastructureRequest) => (
            <div className="flex flex-wrap gap-2">
              <Button
                label="Details"
                className="p-button-sm p-button-outlined"
                onClick={() => setDetail(row)}
              />
              <Button
                label="Modify"
                icon="pi pi-pencil"
                className="p-button-sm"
                disabled={isTerminalStatus(row.status)}
                onClick={() => startModify(row)}
              />
              <Button
                label="Request destruction"
                icon="pi pi-trash"
                className="p-button-sm p-button-danger p-button-outlined"
                disabled={isTerminalStatus(row.status)}
                loading={destroyingId === row.id}
                onClick={() => openDestroyDialog(row)}
              />
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header={`Infrastructure #${detail?.id || ""}`}
        visible={!!detail}
        style={{ width: "50rem" }}
        onHide={() => setDetail(null)}
        footer={
          detail ? (
            <div className="flex gap-2 justify-content-end">
              <Button
                label="Request destruction"
                icon="pi pi-trash"
                className="p-button-danger p-button-outlined"
                disabled={isTerminalStatus(detail.status)}
                loading={destroyingId === detail.id}
                onClick={() => {
                  openDestroyDialog(detail);
                  setDetail(null);
                }}
              />
              <Button
                label="Modify components"
                icon="pi pi-pencil"
                disabled={isTerminalStatus(detail.status)}
                onClick={() => {
                  startModify(detail);
                  setDetail(null);
                }}
              />
            </div>
          ) : null
        }
      >
        {detail && (
          <pre
            className="surface-100 p-3 border-round overflow-auto"
            style={{ maxHeight: "28rem" }}
          >
            {formatTerraformForCopy(detail.terraform_json)}
          </pre>
        )}
      </Dialog>

      <Dialog
        header="Request infrastructure destruction"
        visible={!!destroyTarget}
        style={{ width: "32rem" }}
        onHide={closeDestroyDialog}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={closeDestroyDialog}
            />
            <Button
              label="Request destruction"
              icon="pi pi-trash"
              className="p-button-danger"
              disabled={!destroyCodeMatches}
              loading={
                !!destroyTarget && destroyingId === destroyTarget.id
              }
              onClick={() => {
                void submitDestruction();
              }}
            />
          </div>
        }
      >
        {destroyTarget && (
          <div className="flex flex-column gap-3">
            <Message
              severity="warn"
              text={`Notify admins to destroy infrastructure #${destroyTarget.id} for ${projectLabel(destroyTarget.project_reference)}. This also flags any active phishing domains for deregistration.`}
            />
            <div>
              <label className="block mb-1 text-sm font-medium">
                Type the project code to confirm
              </label>
              <InputText
                value={destroyConfirmCode}
                onChange={(e) => setDestroyConfirmCode(e.target.value)}
                className="w-full"
                placeholder={destroyTarget.project_reference}
                autoFocus
              />
              <small className="text-600 block mt-1">
                Enter <code>{destroyTarget.project_reference}</code> exactly to
                enable destruction.
              </small>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ViewInfrastructures;
